import { html, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';

import { PopBaseComponent } from '../../base/pop-base-component';

import type { OverviewKeyFactsContentObject } from './key-facts.model.js';
import { Constants } from './key-facts.constants.js';
import { dateShort } from '../../common/utils/formatDate.js';
import { isKeyFactWithRank } from '../../shared/key-fact/key-fact.utils.js';
import style from './key-facts.scss?inline';

/**
 * @tag pop-overview-key-facts-component
 * @attribute {OverviewKeyFactsContentObject} [data-component-object] Key facts content
 */
@customElement(Constants.TAG_NAME)
export class PopOverviewKeyFactsComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  static nestedComponents = Constants.NESTED_COMPONENTS;

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  componentObject?: OverviewKeyFactsContentObject;

  constructor() {
    super();
  }

  renderKeyFactsGrid() {
    if (this.componentObject?.keyFacts) {
      const sortedKeyFacts = this.componentObject.keyFacts
        .filter((keyFact) => keyFact.value)
        .filter(isKeyFactWithRank)
        .sort((a, b) => parseInt(a.rank) - parseInt(b.rank));
      return html`
        ${sortedKeyFacts.map(
          (keyFact) => html`
            <div class="overview-key-facts-block b-padding--cell-contained">
              <pop-key-fact-component
                data-variant="overview-key-facts-block"
                data-title=${keyFact.title}
                data-content=${keyFact.value}
                data-sub-content=${keyFact.subContent}
                data-subtitle=${keyFact.subtitle}
                data-tooltip=${keyFact.tooltip}
                data-value-format=${keyFact.valueFormat}
                data-disclaimer-code=${keyFact.disclaimerCode}
                data-as-of-date=${keyFact.asOfDate &&
                this.componentObject?.asOfDate &&
                dateShort(keyFact.asOfDate) ===
                  dateShort(this.componentObject?.asOfDate)
                  ? ''
                  : keyFact.asOfDate}
              >
              </pop-key-fact-component>
            </div>
          `,
        )}
      `;
    }

    return html``;
  }

  renderAsOfDateMessage() {
    if (this.componentObject?.asOfDate) {
      const text = `as of ${dateShort(
        this.componentObject.asOfDate,
      )} unless otherwise noted`;
      return html` <div class="overview-as-of-date">
        <span>${text}</span>
      </div>`;
    }

    return html``;
  }

  render() {
    if (
      this.componentObject?.keyFacts &&
      this.componentObject?.keyFacts?.length !== 0
    ) {
      return this.renderComponent(html`
        <section class="key-facts-content b-container">
          <div class="overview-key-facts-title-section">
            <h2
              class="b-margin-block-end--micro--normal b-typography-serif--headline--100 b-font-weight-bold overview-key-facts-title"
            >
              Key facts and figures
            </h2>
            ${this.renderAsOfDateMessage()}
          </div>
          <div class="overview-key-facts">${this.renderKeyFactsGrid()}</div>
        </section>
      `);
    } else {
      return html``;
    }
  }
}
